import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { offerService } from '../../services/offerService';

const OFFER_TYPES = [
  { value: 'discount', label: 'Discount' },
  { value: 'flash', label: 'Flash' },
  { value: 'gift', label: 'Gift' },
  { value: 'loyalty', label: 'Loyalty' },
];

const defaultFormState = {
  _id: null,
  title: '',
  description: '',
  highlightWord: '',
  code: '',
  type: 'discount',
  discount: '',
  minPurchase: '',
  validUntil: null,
  isActive: true,
};

const OffersTable = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState(defaultFormState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = useMemo(() => Boolean(formState._id), [formState._id]);

  const loadOffers = async () => {
    try {
      setLoading(true);
      const response = await offerService.getOffers();
      setOffers(response.offers || []);
    } catch (error) {
      console.error('Failed to load offers', error);
      toast.error(error.response?.data?.message || 'Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOffers();
  }, []);

  const openDialog = (offer = null) => {
    if (offer) {
      setFormState({
        _id: offer._id,
        title: offer.title || '',
        description: offer.description || '',
        highlightWord: offer.highlightWord || '',
        code: offer.code || '',
        type: offer.type || 'discount',
        discount: offer.discount ?? '',
        minPurchase: offer.minPurchase ?? '',
        validUntil: offer.validUntil ? new Date(offer.validUntil) : null,
        isActive: Boolean(offer.isActive),
      });
    } else {
      setFormState(defaultFormState);
    }
    setErrors({});
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setFormState(defaultFormState);
    setErrors({});
  };

  const handleFieldChange = (field) => (event) => {
    const value =
      event && event.target !== undefined ? event.target.value : event;
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSwitchChange = (field) => (event) => {
    setFormState((prev) => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const validateForm = () => {
    const currentErrors = {};
    if (!formState.title.trim()) {
      currentErrors.title = 'Title is required';
    } else if (formState.title.trim().length > 60) {
      currentErrors.title = 'Title must be 60 characters or less';
    }

    if (!formState.description.trim()) {
      currentErrors.description = 'Description is required';
    } else if (formState.description.trim().length > 120) {
      currentErrors.description = 'Description must be 120 characters or less';
    }

    if (formState.highlightWord && formState.highlightWord.trim().length > 60) {
      currentErrors.highlightWord = 'Highlight word must be 60 characters or less';
    }

    if (formState.code && formState.code.trim().length > 40) {
      currentErrors.code = 'Code must be 40 characters or less';
    }

    if (formState.discount !== '' && (formState.discount < 0 || formState.discount > 100)) {
      currentErrors.discount = 'Discount must be between 0 and 100';
    }

    if (formState.minPurchase !== '' && formState.minPurchase < 0) {
      currentErrors.minPurchase = 'Minimum purchase must be 0 or greater';
    }

    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const payload = {
      title: formState.title.trim(),
      description: formState.description.trim(),
      highlightWord: formState.highlightWord?.trim() || undefined,
      code: formState.code?.trim() || undefined,
      type: formState.type,
      discount: formState.discount === '' ? undefined : Number(formState.discount),
      minPurchase: formState.minPurchase === '' ? undefined : Number(formState.minPurchase),
      validUntil: formState.validUntil ? formState.validUntil.toISOString() : undefined,
      isActive: formState.isActive,
    };

    try {
      setIsSubmitting(true);
      if (isEditMode) {
        await offerService.updateOffer(formState._id, payload);
        toast.success('Offer updated successfully');
      } else {
        await offerService.createOffer(payload);
        toast.success('Offer created successfully');
      }
      closeDialog();
      loadOffers();
    } catch (error) {
      console.error('Failed to save offer', error);
      const message =
        error.response?.data?.errors?.[0]?.message ||
        error.response?.data?.message ||
        'Failed to save offer';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (offer) => {
    if (!window.confirm(`Delete offer "${offer.title}"?`)) {
      return;
    }

    try {
      await offerService.deleteOffer(offer._id);
      toast.success('Offer deleted successfully');
      loadOffers();
    } catch (error) {
      console.error('Failed to delete offer', error);
      toast.error(error.response?.data?.message || 'Failed to delete offer');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Offers Settings
        </Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => openDialog()}>
          Add Offer
        </Button>
      </Box>

      <TableContainer component={Box} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Code</TableCell>
              <TableCell align="center">Discount %</TableCell>
              <TableCell align="center">Min Purchase</TableCell>
              <TableCell>Valid Until</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {offers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {loading ? 'Loading offers...' : 'No offers found. Add your first offer above.'}
                </TableCell>
              </TableRow>
            ) : (
              offers.map((offer) => (
                <TableRow key={offer._id}>
                  <TableCell>{offer.title}</TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Typography variant="body2" color="textSecondary">
                      {offer.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={OFFER_TYPES.find((t) => t.value === offer.type)?.label || offer.type}
                    />
                  </TableCell>
                  <TableCell>{offer.code || '—'}</TableCell>
                  <TableCell align="center">{offer.discount ?? '—'}</TableCell>
                  <TableCell align="center">{offer.minPurchase ?? '—'}</TableCell>
                  <TableCell>
                    {offer.validUntil ? format(new Date(offer.validUntil), 'dd MMM yyyy') : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={offer.isActive ? 'success' : 'default'}
                      label={offer.isActive ? 'Active' : 'Inactive'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openDialog(offer)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDelete(offer)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{isEditMode ? 'Edit Offer' : 'Add Offer'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                name="title"
                value={formState.title}
                onChange={handleFieldChange('title')}
                error={Boolean(errors.title)}
                helperText={errors.title || `${formState.title.length}/60`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Offer Type</InputLabel>
                <Select
                  value={formState.type}
                  label="Offer Type"
                  onChange={handleFieldChange('type')}
                >
                  {OFFER_TYPES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Description"
                name="description"
                value={formState.description}
                onChange={handleFieldChange('description')}
                error={Boolean(errors.description)}
                helperText={errors.description || `${formState.description.length}/120`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Highlight Word"
                name="highlightWord"
                value={formState.highlightWord}
                onChange={handleFieldChange('highlightWord')}
                error={Boolean(errors.highlightWord)}
                helperText={errors.highlightWord || `${formState.highlightWord.length}/60`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Code"
                name="code"
                value={formState.code}
                onChange={(event) =>
                  handleFieldChange('code')({
                    target: { value: event.target.value.toUpperCase() },
                  })
                }
                error={Boolean(errors.code)}
                helperText={errors.code || `${formState.code.length}/40`}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Discount (%)"
                name="discount"
                value={formState.discount}
                onChange={handleFieldChange('discount')}
                error={Boolean(errors.discount)}
                helperText={errors.discount || '0 - 100'}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Minimum Purchase"
                name="minPurchase"
                value={formState.minPurchase}
                onChange={handleFieldChange('minPurchase')}
                error={Boolean(errors.minPurchase)}
                helperText={errors.minPurchase || ''}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Valid Until"
                  value={formState.validUntil}
                  onChange={(date) => setFormState((prev) => ({ ...prev, validUntil: date }))}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formState.isActive}
                    onChange={handleSwitchChange('isActive')}
                  />
                }
                label="Offer is Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditMode ? 'Update Offer' : 'Create Offer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OffersTable;

